import { useNavigate } from 'react-router-dom';

import { NewBadge } from '@/components/new-badge';
import { useIsSoldOut } from '@/hooks/useIsSoldOut';
import { useStockRemaining } from '@/hooks/useStockRemaining';
import { MenuGrouped } from '@/lib/trpc';
import { usePickupDateTimeStore } from '@/stores/pickup-datetime-store';

type Menu = MenuGrouped[number]['menus'][number];

interface MenuListProps {
  menuCategories: MenuGrouped;
}

export default function MenuList({ menuCategories }: MenuListProps) {
  const navigate = useNavigate();
  const { pickupDate } = usePickupDateTimeStore();
  const { isSoldOut } = useIsSoldOut(pickupDate);
  const { isMenuOutOfStock } = useStockRemaining(pickupDate);

  const handleItemClick = (item: Menu) => {
    navigate(`/item/${item.id}`);
  };

  const isMenuItemAvailable = (item: Menu) => {
    // DB의 is_soldout 필드나 daily_menu_status 테이블에서 품절 확인
    return !item.isSoldout && !isSoldOut(item.id) && !isMenuOutOfStock(item.id);
  };

  return (
    <div className="flex-1 bg-white">
      {menuCategories.map((category, index) => (
        <div key={category.id}>
          <div id={`section-${category.name}`}>
            {/* 카테고리 제목 */}
            <div className="bg-white px-5 py-3 mt-5">
              <h2 className="text-lg font-bold text-gray-800">{category.name}</h2>
            </div>

            {/* 해당 카테고리의 메뉴 아이템들 */}
            {category.menus.map((item, itemIndex) => (
              <div key={item.id}>
                <div
                  className={`flex items-center gap-3 px-5 py-5 transition-colors duration-150 ${
                    isMenuItemAvailable(item) ? 'cursor-pointer active:bg-gray-100' : 'cursor-not-allowed opacity-60'
                  } ${itemIndex < category.menus.length - 1 ? 'border-b border-gray-100' : ''}`}
                  onClick={isMenuItemAvailable(item) ? () => handleItemClick(item) : undefined}
                >
                  {/* 메뉴 정보 */}
                  <div className="flex-1">
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-lg font-semibold text-gray-700 leading-[1.44]">
                        <span className="align-middle">{item.name}</span>
                        {item.isNew && <NewBadge />}
                      </h3>
                      <p className="text-sm font-medium text-gray-500 leading-[1.57]">
                        {item.shortDescription || item.description}
                      </p>
                    </div>

                    <div className="flex items-center gap-0.5 mt-1">
                      <span className="text-lg font-semibold text-gray-700">{item.price.toLocaleString()}</span>
                      <span className="text-base font-semibold text-gray-700">원</span>
                    </div>
                  </div>

                  {/* 메뉴 이미지 */}
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-20 h-20 rounded-lg object-cover object-center flex-shrink-0"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* 카테고리 구분선 (마지막 카테고리가 아닌 경우에만) */}
          {index < menuCategories.length - 1 && (
            <div className="relative h-1.5">
              <div className="absolute left-1/2 transform -translate-x-1/2 w-screen h-1.5 bg-[#F0F0F0]" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
